import React, { useState, useEffect, useRef } from "react";
import { Layout, Menu, Avatar, Dropdown, message, Badge, Grid, notification } from "antd";
import { HomeOutlined, AppstoreOutlined, MessageOutlined, BarChartOutlined, UserOutlined, LogoutOutlined, SettingOutlined, ApartmentOutlined, ToolOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { getUnreadCount, getWorkOrderUnreadCount } from "../services/api";
import SiteFooter from "./SiteFooter";

const { Header, Content, Sider } = Layout;

const MainLayout = ({ children, user }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [unreadCount, setUnreadCount] = useState(0);
    const [workOrderUnreadCount, setWorkOrderUnreadCount] = useState(0);
    const lastUnreadCount = useRef(0);
    const unreadInitialized = useRef(false);
    const lastWorkOrderUnreadCount = useRef(0);
    const workOrderUnreadInitialized = useRef(false);
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const canUseWorkOrders = ["user", "agent", "landlord", "sub_account"].includes(user?.role);

    useEffect(() => {
        let active = true;
        const loadUnreadCount = async () => {
            if (!localStorage.getItem("token")) return;
            const [messageResult, workOrderResult] = await Promise.allSettled([
                getUnreadCount(),
                canUseWorkOrders ? getWorkOrderUnreadCount() : Promise.resolve({ count: 0 })
            ]);
            if (messageResult.status === "fulfilled") {
                const data = messageResult.value;
                const nextCount = data.count || 0;
                if (!active) return;
                setUnreadCount(nextCount);
                if (nextCount > 0 && (!unreadInitialized.current || nextCount > lastUnreadCount.current)) {
                    notification.open({
                        message: "您有新消息",
                        description: `当前有 ${nextCount} 条未读消息，点击查看`,
                        icon: <MessageOutlined style={{ color: "#ff4d4f" }} />,
                        duration: 5,
                        onClick: () => navigate("/messages")
                    });
                }
                lastUnreadCount.current = nextCount;
                unreadInitialized.current = true;
            }
            if (canUseWorkOrders && workOrderResult.status === "fulfilled") {
                const nextCount = workOrderResult.value.count || 0;
                if (!active) return;
                setWorkOrderUnreadCount(nextCount);
                if (nextCount > 0 && (!workOrderUnreadInitialized.current || nextCount > lastWorkOrderUnreadCount.current)) {
                    notification.open({
                        message: "您有新工单动态",
                        description: `当前有 ${nextCount} 条工单待查看，点击进入工单中心`,
                        icon: <ToolOutlined style={{ color: "#ff4d4f" }} />,
                        duration: 5,
                        onClick: () => navigate("/work-orders")
                    });
                }
                lastWorkOrderUnreadCount.current = nextCount;
                workOrderUnreadInitialized.current = true;
            } else if (!canUseWorkOrders && active) {
                setWorkOrderUnreadCount(0);
            }
        };
        loadUnreadCount();
        const interval = setInterval(loadUnreadCount, 10000);
        const refresh = () => loadUnreadCount();
        const onVisibilityChange = () => { if (!document.hidden) loadUnreadCount(); };
        window.addEventListener("focus", refresh);
        window.addEventListener("message-unread-refresh", refresh);
        window.addEventListener("work-order-unread-refresh", refresh);
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => {
            active = false;
            clearInterval(interval);
            window.removeEventListener("focus", refresh);
            window.removeEventListener("message-unread-refresh", refresh);
            window.removeEventListener("work-order-unread-refresh", refresh);
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, [navigate, canUseWorkOrders]);

    const getMenuItems = (mobile = false) => {
        const items = [{ key: "/", icon: <HomeOutlined />, label: "公共池" }];
        
        // 房东和子账号可以管理房屋
        if (user?.role === "landlord" || user?.role === "sub_account") {
            items.push({ key: "/manage", icon: <AppstoreOutlined />, label: "房屋管理" });
        }
        
        // 只有租户可以看我的住所
        if (user?.role === "user" || user?.role === "agent") {
            items.push({ key: "/my-residence", icon: <ApartmentOutlined />, label: "我的住所" });
        }
        
        // 消息
        items.push({ key: "/messages", icon: <MessageOutlined />, label: <Badge className="message-menu-badge" count={unreadCount} overflowCount={99} offset={[10, -4]}><span style={{ marginRight: unreadCount > 0 ? 10 : 0, color: mobile ? "inherit" : "#fff" }}>消息</span></Badge> });

        // 入住工单仅供租户与对应房东（含授权子账号）使用，管理员不参与。
        if (canUseWorkOrders) {
            items.push({ key: "/work-orders", icon: <ToolOutlined />, label: <Badge className="message-menu-badge" count={workOrderUnreadCount} overflowCount={99} offset={[10, -4]}><span style={{ marginRight: workOrderUnreadCount > 0 ? 10 : 0, color: mobile ? "inherit" : "#fff" }}>工单</span></Badge> });
        }

        // 所有已登录账号均可进入个人中心和联系客服。
        items.push({ key: "/profile", icon: <UserOutlined />, label: "个人中心" });
        
        // 房东专属功能
        if (user?.role === "landlord") {
            items.push({ key: "/statistics", icon: <BarChartOutlined />, label: "数据统计" });
            items.push({ key: "/sub-accounts", icon: <UserOutlined />, label: "子账号管理" });
        }
        
        // 管理员专属
        if (user?.role === "admin") {
            items.push({ key: "/statistics", icon: <BarChartOutlined />, label: "数据统计" });
            items.push({ key: "/admin", icon: <SettingOutlined />, label: "管理后台" });
        }
        
        return items;
    };

    const userMenu = {
        items: [
            { key: "profile", icon: <UserOutlined />, label: "个人中心" },
            { type: "divider" },
            { key: "logout", icon: <LogoutOutlined />, label: "退出登录", danger: true }
        ].filter(Boolean),
        onClick: ({ key }) => {
            if (key === "profile") {
                navigate("/profile");
            } else if (key === "logout") {
                localStorage.removeItem("token");
                localStorage.removeItem("user");
                navigate("/login");
                message.success("已退出登录");
            }
        }
    };

    const getRoleTag = () => {
        if (user?.role === "admin") return <span style={{ marginLeft: 8, color: "#ff4d4f", fontSize: 12 }}>[管理员]</span>;
        if (user?.role === "landlord") return <span style={{ marginLeft: 8, color: "#1890ff", fontSize: 12 }}>[房东]</span>;
        if (user?.role === "sub_account") return <span style={{ marginLeft: 8, color: "#52c41a", fontSize: 12 }}>[子账号]</span>;
        if (user?.role === "agent") return <span style={{ marginLeft: 8, color: "#722ed1", fontSize: 12 }}>[中介]</span>;
        return null;
    };

    const activeMenuKey = location.pathname.startsWith("/room/") ? "/" : location.pathname.startsWith("/admin") ? "/admin" : location.pathname;
    const mobileMenuItems = getMenuItems(true);

    return (
        <Layout style={{ minHeight: "100vh" }}>
            {!isMobile && <Sider breakpoint="md" collapsedWidth={0} zeroWidthTriggerStyle={{ top: 12, zIndex: 10 }}>
                <div style={{ height: 32, margin: 16, background: "rgba(255,255,255,0.2)", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: "bold" }}>
                    {user?.role === "admin" ? "看屋子后台" : "看屋子"}
                </div>
                <Menu theme="dark" selectedKeys={[location.pathname === "/admin" ? "/admin" : location.pathname]} mode="inline" items={getMenuItems()} onClick={({ key }) => navigate(key)} />
            </Sider>}
            <Layout style={{ minWidth: 0 }}>
                <Header className="main-status-header" style={{ background: "#fff", padding: isMobile ? "0 12px" : "0 24px", gap: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: 16, fontWeight: "bold" }}>{isMobile ? "看屋子" : user?.role === "admin" ? "看屋子 · 管理后台" : "看屋子房屋管理"}</div>
                    <Dropdown menu={userMenu} placement="bottomRight">
                        <div style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
                            <Avatar icon={<UserOutlined />} />
                            <span style={{ marginLeft: 8, maxWidth: isMobile ? 80 : 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.username}</span>
                            {!isMobile && getRoleTag()}
                        </div>
                    </Dropdown>
                </Header>
                {isMobile && (
                    <nav className="mobile-top-nav" aria-label="主要功能">
                        {mobileMenuItems.map(item => (
                            <button key={item.key} type="button" className={activeMenuKey === item.key ? "active" : ""} onClick={() => navigate(item.key)}>
                                {item.icon}
                                <span>{item.label}</span>
                            </button>
                        ))}
                    </nav>
                )}
                <Content className="main-page-content" style={{ margin: isMobile ? "12px 8px" : "24px 16px", padding: isMobile ? 12 : 24, background: "#fff", minHeight: 280, overflowX: "auto", borderRadius: 8 }}>{children}</Content>
                <SiteFooter />
            </Layout>
        </Layout>
    );
};

export default MainLayout;


